import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { FileText, Download, Calendar, Clock, Trash2, Plus, Play, Pause, RefreshCw, Inbox, Settings2, Filter, ClipboardList, Sparkles, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { buildRichReport, buildSavingsReport, getSavedFilters } from '@/lib/reportBuilder';
import { useUser } from '@/lib/UserContext';

const PAGE_OPTIONS = [
  { value: 'executive-digest', label: 'Executive Monday Digest' },
  { value: 'dashboard', label: 'Dashboard Overview' },
  { value: 'spend-analysis', label: 'Spend Analysis' },
  { value: 'spend-distribution', label: 'Spend Distribution' },
  { value: 'category-console', label: 'Category Console' },
  { value: 'commonality', label: 'Commonality Analysis' },
  { value: 'comparative', label: 'Comparative Analysis' },
  { value: 'p2p-analytics', label: 'P2P Analytics' },
  { value: 'industry-benchmark', label: 'Industry Benchmark' },
  { value: 'supplier-directory', label: 'Supplier Directory' },
  { value: 'suppliers', label: 'Supplier Management' },
  { value: 'forecasting', label: 'Spend Forecasting' },
  { value: 'data-quality', label: 'Data Quality' },
  { value: 'governance', label: 'Governance' },
];

const FILTER_KEYS = ['category', 'supplier', 'region', 'country', 'sbu', 'fiscal_year'];

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatDate(iso) {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function FrequencyBadge({ frequency, customDays }) {
  const label = frequency === 'custom' ? `Every ${customDays} days` : frequency;
  const variant = frequency === 'weekly' ? 'default' : frequency === 'monthly' ? 'secondary' : 'outline';
  return <Badge variant={variant} className="capitalize">{label}</Badge>;
}

function FilterPresetSummary({ filterPresets }) {
  if (!filterPresets || Object.keys(filterPresets).length === 0) return null;
  const parts = [];
  for (const [page, filters] of Object.entries(filterPresets)) {
    const active = Object.entries(filters).filter(([, v]) => v && v !== 'all');
    if (active.length > 0) {
      const pageLabel = PAGE_OPTIONS.find(p => p.value === page)?.label || page;
      parts.push(`${pageLabel}: ${active.map(([k, v]) => `${k}=${v}`).join(', ')}`);
    }
  }
  if (parts.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
      <Filter className="w-3 h-3" />
      <span className="truncate">{parts.join(' | ')}</span>
    </div>
  );
}

// --- Generated Reports (Inbox) ---
function ReportsInbox() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    try {
      const res = await api.get('/reports/generated');
      setReports(res.data);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleDownload = async (report) => {
    try {
      const res = await api.get(`/reports/generated/${report.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      // Sanitise filename: keep only ASCII-safe chars, replace everything else with underscore
      const safeName = (report.name || 'report').replace(/[^\w\-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      const dateTag = report.created_at ? report.created_at.slice(0, 10) : '';
      const link = document.createElement('a');
      link.href = url;
      link.download = dateTag ? `${safeName}_${dateTag}.pdf` : `${safeName}.pdf`;
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      // Defer cleanup so the browser has time to start the download
      setTimeout(() => {
        try {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch {}
      }, 1500);
      toast.success('Report downloaded');
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Download failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reports/generated/${id}`);
      setReports(prev => prev.filter(r => r.id !== id));
      toast.success('Report deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  if (reports.length === 0) return (
    <div className="text-center py-12">
      <Inbox className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
      <p className="font-semibold mb-1">No Reports Yet</p>
      <p className="text-sm text-muted-foreground">Generate a report or set up a schedule to see reports here</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {reports.map((report, i) => {
        const isActionPlan = report.type === 'action_plan';
        const isAIInsights = report.type === 'ai_insights';
        const isCategoryStrategy = report.type === 'category_strategy';
        return (
        <motion.div key={report.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
          <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary/50 transition-colors" data-testid={`report-item-${report.id}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActionPlan ? 'bg-amber-500/10' : isAIInsights ? 'bg-purple-500/10' : isCategoryStrategy ? 'bg-emerald-500/10' : 'bg-primary/10'}`}>
                {isActionPlan ? <ClipboardList className="w-5 h-5 text-amber-600" /> : isAIInsights ? <Sparkles className="w-5 h-5 text-purple-600" /> : isCategoryStrategy ? <Sparkles className="w-5 h-5 text-emerald-600" /> : <FileText className="w-5 h-5 text-primary" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm">{report.name}</p>
                  {isActionPlan && <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600">Action Plan</Badge>}
                  {isAIInsights && <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-400 text-purple-600">AI Insights</Badge>}
                  {isCategoryStrategy && <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-400 text-emerald-600">Category Strategy</Badge>}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  <span>{formatDate(report.created_at)}</span>
                  <span className="text-border">|</span>
                  <span>{formatBytes(report.file_size)}</span>
                  {!isActionPlan && (<><span className="text-border">|</span>
                  <span>{report.pages?.length} page{report.pages?.length !== 1 ? 's' : ''}</span></>)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleDownload(report)} data-testid={`download-report-${report.id}`}>
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(report.id)} data-testid={`delete-report-${report.id}`}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          </div>
        </motion.div>
        );
      })}
    </div>
  );
}

// --- Filter Preset Editor ---
function FilterPresetEditor({ pages, presets, onChange }) {
  const [filterOptions, setFilterOptions] = useState(null);

  useEffect(() => {
    api.get('/analytics/filter-options').then(res => setFilterOptions(res.data)).catch(() => {});
  }, []);

  if (!filterOptions || pages.length === 0) return null;

  const updatePreset = (page, key, value) => {
    const newPresets = { ...presets };
    if (!newPresets[page]) newPresets[page] = {};
    newPresets[page][key] = value === 'all' ? null : value;
    onChange(newPresets);
  };

  return (
    <div className="space-y-3 mt-3 p-3 rounded-lg bg-muted/30 border border-dashed border-border">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Settings2 className="w-4 h-4 text-muted-foreground" />
        Filter Presets (per page)
      </div>
      {pages.map(pageVal => {
        const pageLabel = PAGE_OPTIONS.find(p => p.value === pageVal)?.label || pageVal;
        const pagePreset = presets[pageVal] || {};
        return (
          <div key={pageVal} className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{pageLabel}</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {FILTER_KEYS.map(key => {
                const opts = key === 'category' ? filterOptions.categories :
                  key === 'region' ? filterOptions.regions :
                  key === 'country' ? filterOptions.countries :
                  key === 'sbu' ? filterOptions.entities :
                  key === 'fiscal_year' ? filterOptions.fiscalYears :
                  key === 'supplier' ? filterOptions.suppliers?.slice(0, 30) : [];
                if (!opts || opts.length === 0) return null;
                return (
                  <Select key={key} value={pagePreset[key] || 'all'} onValueChange={v => updatePreset(pageVal, key, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`preset-${pageVal}-${key}`}>
                      <SelectValue placeholder={`All ${key}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All {key}</SelectItem>
                      {opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- Schedule Manager ---
function ScheduleManager() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', pages: [], frequency: 'weekly', custom_interval_days: 7, filter_presets: {} });

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await api.get('/reports/schedules');
      setSchedules(res.data);
    } catch { toast.error('Failed to load schedules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const togglePage = (page) => {
    setForm(prev => {
      const newPages = prev.pages.includes(page) ? prev.pages.filter(p => p !== page) : [...prev.pages, page];
      const newPresets = { ...prev.filter_presets };
      if (!newPages.includes(page)) delete newPresets[page];
      return { ...prev, pages: newPages, filter_presets: newPresets };
    });
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Please enter a schedule name');
    if (form.pages.length === 0) return toast.error('Select at least one page');
    if (form.frequency === 'custom' && (!form.custom_interval_days || form.custom_interval_days < 1)) return toast.error('Enter a valid interval');

    try {
      const payload = {
        ...form,
        custom_interval_days: form.frequency === 'custom' ? form.custom_interval_days : null,
      };
      await api.post('/reports/schedules', payload);
      toast.success('Schedule created');
      setShowForm(false);
      setForm({ name: '', pages: [], frequency: 'weekly', custom_interval_days: 7, filter_presets: {} });
      fetchSchedules();
    } catch { toast.error('Failed to create schedule'); }
  };

  const handleToggleActive = async (schedule) => {
    try {
      await api.put(`/reports/schedules/${schedule.id}`, { active: !schedule.active });
      fetchSchedules();
      toast.success(schedule.active ? 'Schedule paused' : 'Schedule activated');
    } catch { toast.error('Failed to update schedule'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reports/schedules/${id}`);
      setSchedules(prev => prev.filter(s => s.id !== id));
      toast.success('Schedule deleted');
    } catch { toast.error('Failed to delete schedule'); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-4">
      {!showForm && (
        <Button onClick={() => setShowForm(true)} data-testid="create-schedule-button">
          <Plus className="w-4 h-4 mr-2" /> New Schedule
        </Button>
      )}

      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Create Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Schedule Name</label>
                <Input
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Weekly Exec Summary"
                  data-testid="schedule-name-input"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Pages to Include</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {PAGE_OPTIONS.map(page => (
                    <label key={page.value} className="flex items-center gap-2 p-2 rounded-md border border-border hover:bg-muted/50 cursor-pointer text-sm">
                      <Checkbox
                        checked={form.pages.includes(page.value)}
                        onCheckedChange={() => togglePage(page.value)}
                        data-testid={`page-checkbox-${page.value}`}
                      />
                      {page.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Frequency</label>
                  <Select value={form.frequency} onValueChange={v => setForm(prev => ({ ...prev, frequency: v }))}>
                    <SelectTrigger data-testid="frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="custom">Custom Interval</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.frequency === 'custom' && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Interval (days)</label>
                    <Input type="number" min="1" value={form.custom_interval_days}
                      onChange={e => setForm(prev => ({ ...prev, custom_interval_days: parseInt(e.target.value) || 1 }))}
                      data-testid="custom-interval-input" />
                  </div>
                )}
              </div>

              {/* Filter Presets */}
              {form.pages.length > 0 && (
                <FilterPresetEditor
                  pages={form.pages}
                  presets={form.filter_presets}
                  onChange={fp => setForm(prev => ({ ...prev, filter_presets: fp }))}
                />
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={handleCreate} data-testid="save-schedule-button">Save Schedule</Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {schedules.length === 0 && !showForm ? (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="font-semibold mb-1">No Schedules</p>
          <p className="text-sm text-muted-foreground">Create a schedule to automate report generation</p>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map((schedule, i) => (
            <motion.div key={schedule.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
              <div className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${schedule.active ? 'border-border hover:border-primary/50' : 'border-border/50 opacity-60'}`} data-testid={`schedule-item-${schedule.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${schedule.active ? 'bg-chart-2/10' : 'bg-muted'}`}>
                    <Clock className={`w-5 h-5 ${schedule.active ? 'text-chart-2' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm">{schedule.name}</p>
                      <FrequencyBadge frequency={schedule.frequency} customDays={schedule.custom_interval_days} />
                      {!schedule.active && <Badge variant="outline" className="text-xs">Paused</Badge>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span>{schedule.pages?.length} page{schedule.pages?.length !== 1 ? 's' : ''}</span>
                    </div>
                    <FilterPresetSummary filterPresets={schedule.filter_presets} />
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span>Next: {formatDate(schedule.next_run)}</span>
                      {schedule.last_run && <span>Last: {formatDate(schedule.last_run)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleToggleActive(schedule)} title={schedule.active ? 'Pause' : 'Activate'} data-testid={`toggle-schedule-${schedule.id}`}>
                    {schedule.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(schedule.id)} data-testid={`delete-schedule-${schedule.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Generate Now ---
function GenerateNow({ onGenerated }) {
  const user = useUser();
  const [name, setName] = useState('');
  const [pages, setPages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [usePageFilters, setUsePageFilters] = useState(true);

  const togglePage = (page) => {
    setPages(prev => prev.includes(page) ? prev.filter(p => p !== page) : [...prev, page]);
  };

  const getActiveFilterCount = (page) => {
    const f = getSavedFilters(page);
    return Object.keys(f).length;
  };

  const handleGenerate = async () => {
    if (!name.trim()) return toast.error('Enter a report name');
    if (pages.length === 0) return toast.error('Select at least one page');
    setGenerating(true);
    try {
      const pageFilters = {};
      if (usePageFilters) {
        pages.forEach(p => { pageFilters[p] = getSavedFilters(p); });
      }
      const pdfBlob = await buildRichReport(api, name, pages, pageFilters, user?.organization_name || '');
      toast.success('Rich report generated! Check your downloads.');
      // Upload the rich PDF to backend inbox
      try {
        const formData = new FormData();
        formData.append('file', pdfBlob, `${name.replace(/\s+/g, '_')}.pdf`);
        formData.append('name', name);
        formData.append('pages', JSON.stringify(pages));
        await api.post('/reports/upload-rich', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch {}
      setName('');
      setPages([]);
      onGenerated?.();
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate report');
    }
    finally { setGenerating(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Generate Rich Report</CardTitle>
        <CardDescription>Create a comprehensive report with charts, data tables, and AI insights from selected pages. Filters applied on each analytics page are automatically captured.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Report Name</label>
          <Input value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Q1 Spend Review"
            data-testid="generate-report-name" />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Pages to Include</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {PAGE_OPTIONS.map(page => {
              const filterCount = getActiveFilterCount(page.value);
              return (
                <label key={page.value} className="flex items-center gap-2 p-2 rounded-md border border-border hover:bg-muted/50 cursor-pointer text-sm relative">
                  <Checkbox checked={pages.includes(page.value)} onCheckedChange={() => togglePage(page.value)}
                    data-testid={`gen-page-checkbox-${page.value}`} />
                  <span>{page.label}</span>
                  {filterCount > 0 && usePageFilters && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-auto">
                      <Filter className="w-2.5 h-2.5 mr-0.5" />{filterCount}
                    </Badge>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={usePageFilters} onCheckedChange={v => setUsePageFilters(v)}
            data-testid="use-page-filters-checkbox" />
          Use filters from analytics pages
          <span className="text-xs text-muted-foreground">(set filters on each page, then generate here)</span>
        </label>

        <Button onClick={handleGenerate} disabled={generating} className="w-full" size="lg" data-testid="generate-now-button">
          <FileText className="w-4 h-4 mr-2" />
          {generating ? 'Generating Report...' : 'Generate Rich Report'}
        </Button>
      </CardContent>
    </Card>
  );
}

// --- Generate Executive Monday Digest (one-click, CPO briefing) ---
function GenerateExecutiveDigest({ onGenerated }) {
  const user = useUser();
  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const hasExecutiveDigest = isSuperAdmin || (user?.subscribed_features || []).includes('executive_digest');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      const name = `Executive Monday Digest — ${today}`;
      await api.post('/reports/generate', {
        name,
        pages: ['executive-digest'],
        filter_presets: {},
      });
      toast.success('Executive Monday Digest generated — check the Inbox.');
      onGenerated?.();
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate digest');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="mt-4 border-l-4 border-l-purple-500" data-testid="executive-digest-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Executive Monday Digest
              {!hasExecutiveDigest && <Badge variant="outline" className="text-[10px] ml-1">Premium</Badge>}
            </CardTitle>
            <CardDescription>
              {hasExecutiveDigest
                ? 'One headline signal per analytics area in a single branded PDF — perfect for CPO Monday stand-ups. Tip: schedule it weekly below to run every Monday automatically.'
                : 'Premium feature. One headline signal per analytics area in a single branded PDF — perfect for CPO Monday stand-ups. Contact your administrator to enable.'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Button
          onClick={handleGenerate}
          disabled={generating || !hasExecutiveDigest}
          className="w-full bg-purple-600 hover:bg-purple-700"
          size="lg"
          data-testid="generate-executive-digest-btn"
          title={!hasExecutiveDigest ? 'Requires Executive Digest premium feature' : ''}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {!hasExecutiveDigest ? 'Executive Digest — Premium' : (generating ? 'Generating Digest...' : 'Generate Executive Monday Digest')}
        </Button>
      </CardContent>
    </Card>
  );
}

// --- Generate Savings Report (Premium) ---
function GenerateSavingsReport({ onGenerated }) {
  const user = useUser();
  const isSuperAdmin = (user?.roles || []).includes('super_admin');
  const hasSavingsReport = isSuperAdmin || (user?.subscribed_features || []).includes('savings_report');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const pdfBlob = await buildSavingsReport(api, 'Savings Potential Report');
      toast.success('Savings report generated!');
      try {
        const formData = new FormData();
        formData.append('file', pdfBlob, 'Savings_Potential_Report.pdf');
        formData.append('name', 'Savings Potential Report');
        formData.append('pages', JSON.stringify(['savings']));
        await api.post('/reports/upload-rich', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch {}
      onGenerated?.();
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate savings report');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="mt-4" data-testid="savings-report-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" />
              Savings Potential Report
            </CardTitle>
            <CardDescription>AI-powered savings analysis with breakdown by area, potential estimates, and recommended actions</CardDescription>
          </div>
          {!hasSavingsReport && <Lock className="w-5 h-5 text-muted-foreground" />}
        </div>
      </CardHeader>
      <CardContent>
        {hasSavingsReport ? (
          <Button onClick={handleGenerate} disabled={generating} className="w-full" size="lg" data-testid="generate-savings-report-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            {generating ? 'Generating Savings Report...' : 'Generate Savings Potential Report'}
          </Button>
        ) : (
          <div className="flex flex-col items-center py-4 text-center" data-testid="savings-report-locked">
            <Lock className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">This report requires a premium subscription. Contact your administrator to enable the Savings Potential Report feature.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Main Reports Page ---
export default function Reports() {
  const [activeTab, setActiveTab] = useState('inbox');
  const [refreshKey, setRefreshKey] = useState(0);
  const user = useUser();
  const isSuperAdmin = (user?.roles || []).includes('super_admin');

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground mt-1">Generate rich reports with charts, insights &amp; recommendations</p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                data-testid="download-security-assurance-button"
                title="Super Admin only — internal security architecture document"
                onClick={async () => {
                  try {
                    const res = await api.get('/reports/security-assurance', { responseType: 'blob' });
                    const url = URL.createObjectURL(res.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Security_Assurance_${new Date().toISOString().slice(0, 10)}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 1500);
                    toast.success('Security Assurance Report downloaded');
                  } catch {
                    toast.error('Failed to download Security Assurance Report');
                  }
                }}
              >
                <Download className="w-4 h-4 mr-2" /> Security Assurance
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="download-triage-memo-button"
                title="Super Admin only — internal engineering audit record"
                onClick={async () => {
                  try {
                    const res = await api.get('/reports/code-review-triage', { responseType: 'blob' });
                    const url = URL.createObjectURL(res.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Code_Review_Triage_${new Date().toISOString().slice(0, 10)}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 1500);
                    toast.success('Code Review Triage Memo downloaded');
                  } catch {
                    toast.error('Failed to download Code Review Triage Memo');
                  }
                }}
              >
                <Download className="w-4 h-4 mr-2" /> Code Review Triage
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => setRefreshKey(k => k + 1)} data-testid="refresh-reports-button">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="inbox" data-testid="inbox-tab">
            <Inbox className="w-4 h-4 mr-1.5" /> Inbox
          </TabsTrigger>
          <TabsTrigger value="schedules" data-testid="schedules-tab">
            <Calendar className="w-4 h-4 mr-1.5" /> Schedules
          </TabsTrigger>
          <TabsTrigger value="generate" data-testid="generate-tab">
            <FileText className="w-4 h-4 mr-1.5" /> Generate
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>Generated Reports</CardTitle>
                <CardDescription>Download or manage your previously generated reports</CardDescription>
              </CardHeader>
              <CardContent>
                <ReportsInbox key={`inbox-${refreshKey}-${activeTab}`} />
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="schedules">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>Scheduled Reports</CardTitle>
                <CardDescription>Automate recurring report generation with filter presets</CardDescription>
              </CardHeader>
              <CardContent>
                <ScheduleManager key={`schedules-${refreshKey}`} />
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="generate">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <GenerateExecutiveDigest onGenerated={() => { setActiveTab('inbox'); setRefreshKey(k => k + 1); }} />
            <GenerateNow onGenerated={() => { setActiveTab('inbox'); setRefreshKey(k => k + 1); }} />
            <GenerateSavingsReport onGenerated={() => { setActiveTab('inbox'); setRefreshKey(k => k + 1); }} />
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
